"use client"

import { createContext, useState, useCallback, useContext } from "react"
import { db } from "../firebase-config" // Adjust if needed
import { collection, query, where, orderBy, limit, getDocs, addDoc, deleteDoc, serverTimestamp, doc } from "firebase/firestore";

const LeaderboardContext = createContext()


export const useLeaderboard = () => {
  return useContext(LeaderboardContext)
}

export const LeaderboardProvider = ({ children }) => {
  const [leaderboard, setLeaderboard] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const fetchLeaderboard = useCallback(async (mode, limitCount = 20, difficulty = null) => {
    if (process.env.NODE_ENV === "development") console.log(`Fetching leaderboard for mode: ${mode}, difficulty: ${difficulty || "all"}, limit: ${limitCount}`)
    setIsLoading(true)
    setError(null)

    try {
      // Create constraints array based on mode and difficulty
      const constraints = [where("mode", "==", mode)]

      // Add difficulty filter if provided and not in daily challenge mode
      if (difficulty && mode !== "daily") {
        constraints.push(where("difficulty", "==", difficulty))
      }

      // Add ordering and limit
      constraints.push(orderBy("time"))
      constraints.push(limit(limitCount))

      try {
        const q = query(collection(db, "scores"), ...constraints)
        const querySnapshot = await getDocs(q)
        const data = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        if (process.env.NODE_ENV === "development") console.log(`Leaderboard data (${data.length} entries):`, data)
        setLeaderboard(data)
      } catch (indexErr) {
        console.error("Index error:", indexErr)

        // Handle index error and fallback logic
        if (indexErr.message && indexErr.message.includes("index")) {
          if (process.env.NODE_ENV === "development") console.log("Index error, falling back to simple query")

          // Simpler query without ordering
          const simpleConstraints = [where("mode", "==", mode)]
          if (difficulty && mode !== "daily") {
            simpleConstraints.push(where("difficulty", "==", difficulty))
          }

          const simpleQuery = query(collection(db, "scores"), ...simpleConstraints)
          const querySnapshot = await getDocs(simpleQuery)
          const data = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))

          // Sort and limit data manually
          data.sort((a, b) => {
            const timeDiff = a.time - b.time
            if (timeDiff !== 0) return timeDiff
            if (a.timestamp && b.timestamp) {
              return a.timestamp.seconds - b.timestamp.seconds
            }
            return 0
          })

          const limitedData = data.slice(0, limitCount)
          if (process.env.NODE_ENV === "development") console.log(`Leaderboard data (fallback, ${limitedData.length} entries):`, limitedData)
          setLeaderboard(limitedData)

          if (data.length === 0) {
            setError("For optimal performance, please create the required index.")
          }
        } else {
          throw indexErr
        }
      }
    } catch (err) {
      console.error("Error fetching leaderboard:", err)
      setError(err.message || "Failed to fetch leaderboard data")
      setLeaderboard([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  const submitScore = useCallback(async (playerName, time, mode, difficulty, moves) => {
    if (process.env.NODE_ENV === "development") console.log("Submitting score:", { playerName, time, mode, difficulty, moves })
    setIsLoading(true)
    try {
      const gameId = `${playerName}-${mode}-${difficulty}-${time.toFixed(6)}`
  
      const submittedGames = JSON.parse(localStorage.getItem("submittedGames") || "[]")
  
      if (submittedGames.includes(gameId)) {
        if (process.env.NODE_ENV === "development") console.log("This game has already been submitted (exact match)")
        setError("This game has already been submitted to the leaderboard")
        setIsLoading(false)
        return false
      }
  
      // 🔥 NEW: Check if username already exists in this mode/difficulty
      const duplicateNameCheck = query(
        collection(db, "scores"),
        where("playerName", "==", playerName),
        where("mode", "==", mode),
        where("difficulty", "==", difficulty),
      )
  
      const duplicateSnapshot = await getDocs(duplicateNameCheck)
  
      if (!duplicateSnapshot.empty) {
        if (process.env.NODE_ENV === "development") console.log("Username already exists for this mode/difficulty. Deleting old score.")
        const oldDocId = duplicateSnapshot.docs[0].id
        await deleteDoc(doc(db, "scores", oldDocId))
        if (process.env.NODE_ENV === "development") console.log(`Deleted old score with ID: ${oldDocId}`)
      }
  
      // 🔥 Proceed to submit new score to leaderboard
      const docRef = await addDoc(collection(db, "scores"), {
        playerName,
        time: Number(time.toFixed(6)),
        mode,
        difficulty,
        moves,
        gameId,
        timestamp: serverTimestamp(),
      })

      // ALSO save to 'games' collection for analytics
      await addDoc(collection(db, "games"), {
        playerName,
        time: Number(time.toFixed(6)),
        mode,
        difficulty,
        moves,
        timestamp: serverTimestamp(),
        isDailyChallenge: mode === 'daily',
        isLeaderboardSubmission: true
      })
  
      submittedGames.push(gameId)
      localStorage.setItem("submittedGames", JSON.stringify(submittedGames))
  
      if (process.env.NODE_ENV === "development") console.log("Score submitted with ID:", docRef.id)
      setIsLoading(false)
      return true
    } catch (err) {
      console.error("Error submitting score:", err)
      setError(err.message || "Failed to submit score")
      setIsLoading(false)
      return false
    }
  }, [])
  

  const value = {
    leaderboard,
    isLoading,
    error,
    fetchLeaderboard,
    submitScore,
    setIsLoading,
  }

  return <LeaderboardContext.Provider value={value}>{children}</LeaderboardContext.Provider>
}

